import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary } from './Overlay';
import { Fieldset, SettingRow, Switch, Field } from './Form';
import { text } from './peopleCopy';

/*
 * Cancel reasons — layar `reasons` paket client.
 *
 * Prototipe punya tiga sisi: Customer, Provider, Admin. CancelReason.userType
 * hanya mengenal 1 dan 2. Alasan yang dipakai admin adalah daftar tetap di
 * dalam kode dashboard, jadi menampilkan sisi ketiga di sini akan menawarkan
 * pilihan yang tidak bisa disimpan. Keterbatasannya ditulis di layar.
 *
 * Kolom "Times used" dicocokkan lewat teks, bukan kunci asing — lihat
 * getAdminCancelReasons. Karena itu menyunting teks sebuah alasan memutus
 * hitungan lamanya, dan formulirnya memperingatkan sebelum itu terjadi.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String,
                  $userType: Int, $state: String) {
  getAdminCancelReasons(currentPage: $currentPage, limit: $limit, searchList: $searchList,
                        userType: $userType, state: $state) {
    count total activeCount customerCount providerCount unmatchedUses status errorMessage
    results { id reason hasStrayWhitespace userType isActive timesUsed lastUsedAt }
  }
}`;

// updateCancelReason menangani buat DAN sunting: id kosong berarti membuat.
const M_SAVE = `mutation ($id: Int, $userType: Int, $reason: String, $isActive: Boolean) {
  updateCancelReason(id: $id, userType: $userType, reason: $reason, isActive: $isActive) {
    status errorMessage
  }
}`;

const M_DELETE = `mutation ($id: Int) {
  removeCancelReason(id: $id) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;

const SIDE = { 1: 'Customer', 2: 'Provider' };

const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
};

class CancelReasons extends React.Component {
  state = {
    rows: [], count: 0, total: 0, activeCount: 0,
    customerCount: 0, providerCount: 0, unmatched: 0,
    page: 1, perPage: 25, search: '', side: '', filterState: '',
    loading: true, error: null,
    form: null, formError: null, deleting: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    userType: this.state.side ? Number(this.state.side) : undefined,
    state: this.state.filterState || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminCancelReasons;
    if (!ok(res)) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load cancel reasons.', 'تعذر تحميل أسباب الإلغاء.') });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0, total: res.total || 0,
      activeCount: res.activeCount || 0, customerCount: res.customerCount || 0,
      providerCount: res.providerCount || 0, unmatched: res.unmatchedUses || 0,
      loading: false
    });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  pick = key => e => this.setState({ [key]: e.target.value, page: 1 }, () => this.load());

  openAdd = () => this.setState({
    form: { id: null, reason: '', userType: 1, isActive: true, timesUsed: 0, original: '' },
    formError: null, notice: null
  });

  openEdit = r => this.setState({
    form: {
      id: r.id, reason: r.reason, userType: r.userType, isActive: r.isActive,
      timesUsed: r.timesUsed, original: r.reason
    },
    formError: null, notice: null
  });

  setForm = (key, value) => this.setState(st => ({ form: { ...st.form, [key]: value } }));

  submit = async () => {
    const f = this.state.form;
    const reason = f.reason.trim();
    if (!reason) {
      this.setState({ formError: this.tx('The reason text is required.', 'نص السبب مطلوب.') });
      return;
    }
    // Teks yang sama persis dua kali membuat pelanggan melihat pilihan kembar.
    const clash = this.state.rows.find(r =>
      r.id !== f.id && r.userType === Number(f.userType)
      && r.reason.toLowerCase() === reason.toLowerCase());
    if (clash) {
      this.setState({ formError: `${this.tx(SIDE[f.userType] + 's already have that reason.', f.userType === 1 ? 'لدى العملاء هذا السبب بالفعل.' : 'لدى مقدمي الخدمة هذا السبب بالفعل.')}` });
      return;
    }

    this.setState({ saving: true, formError: null });
    const json = await gql(M_SAVE, {
      id: f.id || undefined,
      userType: Number(f.userType),
      // Dipangkas: baris yang sudah ada berakhir dengan tab, dan itulah yang
      // membuat hitungan pemakaiannya tidak pernah cocok tanpa TRIM.
      reason,
      isActive: !!f.isActive
    }).catch(() => null);
    const res = json && json.data && json.data.updateCancelReason;
    this.setState({ saving: false });
    if (!ok(res)) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save the reason.', 'تعذر حفظ السبب.') });
      return;
    }
    this.setState({
      form: null,
      notice: { tone: 'info', text: f.id ? this.tx('Reason updated.', 'تم تحديث السبب.') : this.tx('Reason added.', 'تمت إضافة السبب.') }
    });
    this.load(true);
  };

  remove = async () => {
    const r = this.state.deleting;
    this.setState({ saving: true });
    const json = await gql(M_DELETE, { id: r.id }).catch(() => null);
    const res = json && json.data && json.data.removeCancelReason;
    this.setState({ saving: false, deleting: null });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not delete the reason.', 'تعذر حذف السبب.') }
        : { tone: 'danger', text: this.tx('The reason was deleted. Past cancellations keep their text.', 'تم حذف السبب. تحتفظ الإلغاءات السابقة بنصها.') }
    });
    this.load(true);
  };

  columns() {
    const t = this.tx;
    return [
      {
        key: 'reason', label: t('Reason', 'السبب'), sortable: true,
        render: r => (
          <span>
            {r.reason}
            {r.hasStrayWhitespace && (
              <span className="tag warn" style={{ marginInlineStart: 8 }}>
                <i />{t('Stray whitespace', 'مسافات زائدة')}
              </span>
            )}
          </span>
        )
      },
      {
        key: 'userType', label: t('Shown to', 'يظهر لـ'), sortable: true,
        render: r => r.userType === 1 ? t('Customer', 'العملاء') : r.userType === 2 ? t('Provider', 'مقدمي الخدمة') : <span className="muted">{t('Unknown', 'غير معروف')}</span>
      },
      {
        key: 'timesUsed', label: t('Times used', 'مرات الاستخدام'), align: 'end', sortable: true,
        render: r => r.timesUsed
          ? (
            <span className="num" title={r.lastUsedAt ? `${t('Last used', 'آخر استخدام')} ${day(r.lastUsedAt)}` : ''}>
              {r.timesUsed}
            </span>
          )
          : <span className="muted">{t('Never', 'أبداً')}</span>
      },
      {
        key: 'isActive', label: t('Status', 'الحالة'),
        render: r => (
          <span className={`tag ${r.isActive ? 'ok' : ''}`}>
            <i />{r.isActive ? t('Offered', 'معروض') : t('Hidden', 'مخفي')}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title="Edit reason"
              aria-label={`Edit ${r.reason}`} onClick={() => this.openEdit(r)}>
              <Icon name="edit" />
            </button>
            <button className="btn icon" type="button" title="Delete reason"
              aria-label={`Delete ${r.reason}`}
              onClick={() => this.setState({ deleting: r, notice: null })}>
              <Icon name="trash" />
            </button>
          </span>
        )
      }
    ];
  }

  renderForm() {
    const f = this.state.form;
    const { saving, formError } = this.state;
    const renamed = f.id && f.timesUsed > 0 && f.reason.trim() !== f.original;
    const t = this.tx;

    return (
      <Modal
        icon="x"
        title={f.id ? t('Edit cancel reason', 'تعديل سبب الإلغاء') : t('Add cancel reason', 'إضافة سبب إلغاء')}
        sub={t('Shown as a choice when a booking is cancelled in the apps.', 'يظهر كخيار عند إلغاء حجز في التطبيقات.')}
        confirmLabel={f.id ? t('Save reason', 'حفظ السبب') : t('Add reason', 'إضافة السبب')}
        busy={saving}
        disabled={!f.reason.trim()}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

        <Fieldset title={t('Reason', 'السبب')}>
          <Field label={t('Text shown to the user', 'النص الظاهر للمستخدم')} required span>
            <input className="inp" value={f.reason}
              placeholder="Provider took too long"
              onChange={e => this.setForm('reason', e.target.value)} />
          </Field>
          <Field label={t('Shown to', 'يظهر لـ')}
            hint={t('Admin cancellations use a separate fixed list built into the dashboard.', 'إلغاءات المشرف تستخدم قائمة ثابتة منفصلة داخل لوحة التحكم.') }>
            <select className="inp" value={f.userType}
              onChange={e => this.setForm('userType', Number(e.target.value))}>
              <option value={1}>{t('Customers', 'العملاء')}</option>
              <option value={2}>{t('Providers', 'مقدمي الخدمة')}</option>
            </select>
          </Field>
        </Fieldset>

        {/* Riwayat pemakaian dicocokkan lewat teks, jadi mengganti kalimatnya
            memutus hitungan lamanya. Disebut sebelum disimpan. */}
        {renamed && (
          <div className="notice">
            <span className="ni">i</span>
            <span className="nt">
              {t(`This reason has been used ${f.timesUsed} time${f.timesUsed === 1 ? '' : 's'}. Past cancellations store the old wording, so changing it here resets the count to zero — the old cancellations keep their own text and are not lost.`, `تم استخدام هذا السبب ${f.timesUsed} مرة. تحتفظ الإلغاءات السابقة بالنص القديم، لذلك يؤدي تغييره إلى إعادة العدد إلى صفر — ولا تُفقد الإلغاءات السابقة.`)}
            </span>
          </div>
        )}

        <Fieldset title={t('Availability', 'التوفر')} rows>
          <SettingRow label={t('Offered in the apps', 'معروض في التطبيقات')}
            hint={t('A hidden reason stops appearing in the cancel dialog. Past cancellations keep it.', 'السبب المخفي لا يظهر في نافذة الإلغاء، بينما تحتفظ به الإلغاءات السابقة.') }>
            <Switch checked={f.isActive} label={t('Offered in the apps', 'معروض في التطبيقات')}
              onChange={v => this.setForm('isActive', v)} />
          </SettingRow>
        </Fieldset>
      </Modal>
    );
  }

  render() {
    const {
      rows, count, total, activeCount, customerCount, providerCount, unmatched,
      page, perPage, loading, error, side, filterState,
      form, deleting, saving, notice
    } = this.state;
    const t = this.tx;

    return (
      <AdminShell
        current="reasons"
        breadcrumb={[t('Catalogue', 'الكتالوج'), t('Cancel reasons', 'أسباب الإلغاء')]}
        title={t('Cancel reasons', 'أسباب الإلغاء')}
        lead={this.locale() === 'ar' ? `${total} سبب، ${activeCount} معروض. ${customerCount} للعملاء، ${providerCount} لمقدمي الخدمة.` : `${total} reason${total === 1 ? '' : 's'}, ${activeCount} offered. ${customerCount} for customers, ${providerCount} for providers.`}
        actions={[
          <a className="btn ghost" href="/siteadmin/cancel-reasons" key="c">{t('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn" type="button" key="a" onClick={this.openAdd}>
            <Icon name="plus" />{t('Add reason', 'إضافة سبب')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {t('Only customer and provider reasons are stored here. The list an admin picks from when cancelling a booking is fixed in the dashboard code and cannot be edited on this screen.', 'تُحفظ هنا أسباب العملاء ومقدمي الخدمة فقط. قائمة الأسباب التي يختار منها المشرف عند إلغاء الحجز ثابتة في لوحة التحكم ولا يمكن تعديلها هنا.')}
          </span>
        </div>

        {/* Alasan yang tercatat tapi tidak cocok dengan satu pun baris di sini
            berarti pilihannya pernah diketik bebas, dihapus, atau diubah
            kalimatnya. Angka itu tidak muncul di mana pun sebelum ini. */}
        {!loading && !error && unmatched > 0 && (
          <div className="notice">
            <span className="ni">i</span>
            <span className="nt">
              {this.locale() === 'ar' ? `${unmatched} إلغاء سابق سجّل سبباً لا يطابق أي صف أدناه — نص حر أو سبب حُذف أو عُدّل.` : `${unmatched} past cancellation${unmatched === 1 ? '' : 's'} recorded a reason that matches none of the rows below — free text, or a reason since deleted or reworded.`}
            </span>
          </div>
        )}

        {form && this.renderForm()}

        {deleting && (
          <Modal
            icon="trash" tone="danger"
            title={t('Delete this reason?', 'حذف سبب الإلغاء هذا؟')}
            sub={deleting.reason}
            confirmLabel={t('Delete reason', 'حذف السبب')}
            busy={saving}
            onConfirm={this.remove}
            onClose={() => this.setState({ deleting: null })}
          >
            <Summary pairs={[
              [t('Shown to', 'يظهر لـ'), deleting.userType === 1 ? t('Customer', 'العملاء') : t('Provider', 'مقدمي الخدمة')],
              [t('Times used', 'مرات الاستخدام'), deleting.timesUsed ? `${deleting.timesUsed}` : t('Never', 'أبداً')],
              [t('Last used', 'آخر استخدام'), day(deleting.lastUsedAt) || '—']
            ]} />
            <span className="hint">
              {t('Past cancellations store their own copy of the wording, so the history stays intact. To stop offering it without deleting the row, hide it instead.', 'تحتفظ الإلغاءات السابقة بنسخة من النص، لذلك يبقى السجل محفوظاً. لإيقاف عرضه دون حذف الصف، أخفِه بدلاً من ذلك.')}
            </span>
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t('Search reason text', 'ابحث في نص السبب')}
              filters={
                <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                  <select className="inp" value={side} onChange={this.pick('side')}
                    aria-label={t('Filter by audience', 'تصفية حسب الجمهور')}>
                    <option value="">{t('Shown to: all', 'يظهر لـ: الكل')}</option>
                    <option value="1">{t('Customers', 'العملاء')}</option>
                    <option value="2">{t('Providers', 'مقدمي الخدمة')}</option>
                  </select>
                  <select className="inp" value={filterState} onChange={this.pick('filterState')}
                    aria-label={t('Filter by status', 'تصفية حسب الحالة')}>
                    <option value="">{t('Status: all', 'الحالة: الكل')}</option>
                    <option value="active">{t('Offered', 'معروض')}</option>
                    <option value="inactive">{t('Hidden', 'مخفي')}</option>
                  </select>
                </span>
              }
              emptyText={t('No cancel reasons', 'لا توجد أسباب إلغاء')}
              emptyHint={t('Add one so customers can say why they cancelled.', 'أضف سبباً ليتمكن العملاء من توضيح سبب الإلغاء.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(CancelReasons);
